<template>

    <div class="mb-5 draggable rounded bg-white">
        <!-- Task Header. -->
        <div class="flex mb-px" :style="'background-color: '+task.color">
            <div class="w-6 flex-shrink-0 py-1.5 -my-px" :style="'background-color: '+task.color">
                <i class="fa fa-bars text-sm text-gray-400 hover:text-gray-500 cursor-move pt-1 pl-1 handle"
                   v-if="!estimate.readonly"></i>
            </div>
            <!-- Reference -->
            <div class="w-16 flex m-0 -ml-px">
                <page-cell
                    class="text-sm -m-px px-2 py-1.5 border hover:z-0"
                    :style="'background-color: '+task.color+';border-color: '+task.color"
                    :placeholder="task.defaultReference"
                    :disabled="estimate.readonly"
                    :read-only="estimate.readonly"
                    v-model="task.reference"
                    @input="estimate.update()"
                    @change="estimate.addSnapshot()"
                    ref="reference"
                ></page-cell>
            </div>
            <div class="flex-1">
                <div
                    v-if="estimate.readonly"
                    class="text-sm -ml-px -my-px px-2 py-1.5 shadow-none relative hover:z-0 focus:z-30 resize-none font-bold text-gray-900"
                    :style="'background-color: '+task.color+';border-color: '+task.color"
                    v-text="task.name"
                >
                </div>
                <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'" v-else>
                <textarea
                    placeholder="Untitled task..."
                    class="text-sm -ml-px -my-px px-2 py-1.5 shadow-none relative hover:z-0 focus:z-30 resize-none font-bold text-gray-900"
                    :style="'background-color: '+task.color+';border-color: '+task.color"
                    type="text"
                    ref="name"
                    :disabled="estimate.readonly"
                    v-model="task.name"
                    @input="estimate.update()"
                    @change="estimate.addSnapshot()"
                    rows="1"
                ></textarea>
                </autoresizable-textarea>
            </div>
            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px pl-1 pr-1"
                 :style="'background-color: '+task.color"></div>
            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px pl-1 pr-1" :style="'background-color: '+task.color">
                <ui-dropdown menu-class="p-3 w-56 z-50" :close-on-mouse-out-scroll="true" :key="clipboardKey">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-calculator"
                                          @click="$modals.open('estimate-task-cost-list', { task, section, estimate })">
                            See Cost Breakdown
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                        <ui-dropdown-item icon="fas fa-tint"
                                          @mouseleave="(e) => {$refs['itemcolormenu'].close(e)}"
                                          @mouseenter="openColorSubmenu"
                                          ref="itemcolor"
                                          :submenu="true"
                                          v-if="!estimate.readonly"
                        >
                            Set task color
                            <div class="fixed" style="width:auto;height:auto;overflow: visible"
                                 :ref="'itemcolorrelative'">
                                <vue-context :ref="'itemcolormenu'" :autoFocus="false"
                                             class="inline pb-0 focus:outline-none focus:ring-0 z-30"
                                             :close-on-click="false" :close-on-scroll="false">
                                    <div :id="'itemcolormenu'"
                                         class="p-3 w-48 z-30 bg-white rounded-lg border border-gray-300 shadow-lg">
                                        <div
                                            v-for="(index,key) in ([0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19])"
                                            class="w-8 inline-block items-center p-1.5 border border-none rounded-lg"
                                            :key="index">
                                            <button
                                                class="w-6 h-6 rounded-lg flex-none mt-0.5 mr-2 inline-block border border-gray-300 hover:border-gray-400"
                                                :style="'background-color: '+color(key)"
                                                @click="setColor(task,color(key))">
                                            </button>
                                        </div>
                                    </div>
                                </vue-context>
                            </div>
                        </ui-dropdown-item>
                        <ui-dropdown-divider></ui-dropdown-divider>
                        <ui-dropdown-item icon="fas fa-cut" @click="cutTask();close();" v-if="!estimate.readonly">Cut
                            Task
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-copy" @click="copyTask();close();">Copy Task
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-paste"
                                          v-if="!estimate.readonly && (clipboardTaskData[0])"
                                          @click="pasteTask(clipboardTaskData[0],task);close();">
                            Paste Task
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                        <ui-dropdown-item ref="paste1item"
                                          @mouseleave="closePasteSubMenu(1)"
                                          @mouseenter="openPasteSubMenu(1)"
                                          v-if="!estimate.readonly"
                                          icon="fas fa-clipboard"
                                          :submenu="true"
                        >Library
                            <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                 :ref="'paste1relative'">
                                <vue-context :ref="'paste1menu'" :autoFocus="false"
                                             class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                             :close-on-click="false" :close-on-scroll="false">
                                    <div :id="'paste1menu'"
                                         class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg max-h-96 overflow-x-auto">
                                        <ui-dropdown-item icon="fas fa-plus"
                                                          @click="addToLibrary(task);close();">
                                            Add Task to Library
                                        </ui-dropdown-item>
                                        <ui-dropdown-divider
                                            v-if="!estimate.readonly && (clipboardTaskData.length > 1)"></ui-dropdown-divider>
                                        <ui-dropdown-item icon="fas fa-paste"
                                                          button-class="truncate"
                                                          class="w-full truncate flex"
                                                          v-for="(item,i) in clipboardTaskData"
                                                          :key="'paste1-'+i"
                                                          v-if="i > 0"
                                                          @click="pasteTask(item.data,task);close();">
                                            <div class="text-xs text-blue-500 truncate">{{ item.name }}</div>
                                        </ui-dropdown-item>
                                    </div>
                                </vue-context>
                            </div>
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                        <ui-dropdown-item icon="fas fa-trash" danger @click="deleteTask();close();"
                                          v-if="!estimate.readonly">Delete
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>
            </div>
        </div>

        <!-- Task Items. -->
        <draggable :list="task.items" draggable=".draggable" handle=".handle" group="items"
                   @change="estimate.update();estimate.addSnapshot(); $emit('reflow')">
            <estimate-task-item v-for="(item,index) in task.items"
                                :key="index"
                                :item="item"
                                :task="task"
                                :section="section"
                                :item-clipboard="[]"
                                :store="store"
                                :multimode="multimode"
                                @ping="(e) => $emit('ping',e)"
                                @reflow="$emit('reflow')"
                                :pinging="pinging"
                                :clipboard-key="clipboardKey"
                                :clipboard-item-data="clipboardItemData"
                                :clipboard-task-data="clipboardTaskData"
                                :estimate="estimate" ref="items"></estimate-task-item>
        </draggable>

        <!-- New Task Item. -->
        <div class="flex" v-if="!estimate.readonly">
            <div class="w-6 flex-shrink-0 self-start py-1.5 -my-px border-t">
                <i class="fa fa-plus text-xs text-gray-300 pl-1 pt-1"></i>
            </div>
            <div class="w-16 flex m-0">
                <page-cell
                    class="text-sm -my-px ml-0 px-2 py-1.5 border border-gray-300 bg-white"
                    :placeholder="nextItemReference()"
                    v-model="newItem.reference"
                    @input="addItem('reference')"
                ></page-cell>
            </div>
            <div class="flex-1 -ml-px">
                <input
                    placeholder="Add a new item..."
                    class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-300 shadow-none relative hover:z-10 focus:z-30 resize-none bg-white"
                    type="text"
                    v-model="newItem.description"
                    @input="addItem('description')"
                >
            </div>
            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1"></div>
            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1">
                <ui-dropdown menu-class="p-3 w-56" :close-on-mouse-out-scroll="true" :key="clipboardKey"
                             v-if="!estimate.readonly">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-paste"
                                          v-if="!estimate.readonly && (clipboardItemData[0])"
                                          @click="pasteItem(clipboardItemData[0],false);close();">
                            Paste Item
                        </ui-dropdown-item>
                        <ui-dropdown-divider
                            v-if="!estimate.readonly && (clipboardItemData[0])"></ui-dropdown-divider>
                        <ui-dropdown-item :ref="'paste'+(3)+'item'"
                                          @mouseleave="closePasteSubMenu(3)"
                                          @mouseenter="openPasteSubMenu(3)"
                                          v-if="!estimate.readonly && (clipboardItemData.length > 1)"
                                          icon="fas fa-clipboard"
                                          :submenu="true"
                        >Library
                            <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                 :ref="'paste'+(3)+'relative'">
                                <vue-context :ref="'paste'+(3)+'menu'" :autoFocus="false"
                                             class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                             :close-on-click="false" :close-on-scroll="false">
                                    <div :id="'paste'+(3)+'menu'"
                                         class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg max-h-96 overflow-x-auto">
                                        <ui-dropdown-item :ref="'paste999'+groupIndex+'item'"
                                                          v-for="(group,groupIndex) in groupedClipboardTaskData()"
                                                          :key="'paste999'+groupIndex"
                                                          v-if="group.name"
                                                          @mouseleave="closePasteSubMenu('999'+groupIndex)"
                                                          @mouseenter="openPasteSubMenu('999'+groupIndex)"
                                                          :submenu="true"
                                                          icon=" "
                                        >{{ group.name }}
                                            <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                                 :ref="'paste999'+groupIndex+'relative'">
                                                <vue-context :ref="'paste999'+groupIndex+'menu'" :autoFocus="false"
                                                             class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                                             :close-on-click="false" :close-on-scroll="false">
                                                    <div :id="'paste999'+groupIndex+'menu'"
                                                         class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg max-h-96 overflow-x-auto">
                                                        <ui-dropdown-item icon="fas fa-paste"
                                                                          button-class="truncate"
                                                                          class="w-full truncate flex"
                                                                          v-for="(tem,i) in group.items"
                                                                          :key="'paste999'+groupIndex+'-'+i"
                                                                          @click="pasteItem(tem.data,item);close();">
                                                            <div class="text-xs text-blue-500 truncate">{{
                                                                    tem.name
                                                                }}
                                                            </div>
                                                        </ui-dropdown-item>
                                                    </div>
                                                </vue-context>
                                            </div>
                                        </ui-dropdown-item>
                                        <ui-dropdown-item icon="fas fa-paste"
                                                          button-class="truncate"
                                                          class="w-full truncate flex"
                                                          v-for="(tem,i) in groupedClipboardTaskData().find((a) => a.name === '').items"
                                                          :key="'paste1-'+i"
                                                          @click="pasteItem(tem.data,item);close();">
                                            <div class="text-xs text-blue-500 truncate">{{ tem.name }}</div>
                                        </ui-dropdown-item>
                                    </div>
                                </vue-context>
                            </div>
                        </ui-dropdown-item>
                        <ui-dropdown-divider
                            v-if="!estimate.readonly"></ui-dropdown-divider>
                        <ui-dropdown-item blue icon="fas fa-layer-group text-blue-400"
                                          v-if="!estimate.readonly"
                                          @click="addBundleItem()">
                            Create Bundle
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>

            </div>
        </div>

        <!-- Total. -->
        <div class="flex text-xs text-gray-500 text-right font-semibold">
            <div class="flex-1 px-2 py-1">Task Total</div>
            <div class="w-28 -ml-px px-2 py-1">
                <ui-dropdown placement="bottom-start" menu-class="p-3 w-72 flex" class="flex w-full"
                             trigger-class="flex w-full">
                    <template #default="{ open, close }">
                        <div @mouseenter="open" @mouseleave="close" class="w-full">
                            {{
                                (task.items.reduce((a, item) => a + (item.buildTotalMarkedUp ? item.buildTotalMarkedUp : 0), 0)) - (task.items.reduce((a, item) => a + (item.discount ? item.discount : 0), 0)) | toCurrency
                            }}
                        </div>
                    </template>
                    <template #menu>
                        <table class="w-full">
                            <tbody>
                            <tr>
                                <td class="text-gray-600 text-sm">Material Cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    <div
                                        v-if="task.items.some((i) => i.markupFixedValue)"
                                        class="text-red-400">
                                        ???
                                    </div>
                                    <div v-else>
                                        {{
                                            task.items.reduce((a, item) => a + (item.materialBuildTotal ? item.materialBuildTotal : 0), 0) | toCurrency
                                        }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Labour Cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    <div
                                        v-if="task.items.some((i) => i.markupFixedValue)"
                                        class="text-red-400">
                                        ???
                                    </div>
                                    <div v-else>
                                        {{
                                            task.items.reduce((a, item) => a + (item.labourBuildTotal ? item.labourBuildTotal : 0), 0) | toCurrency
                                        }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Fixed Cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    <div
                                        v-if="task.items.some((i) => i.markupFixedValue)"
                                        class="text-red-400">
                                        ???
                                    </div>
                                    <div v-else>
                                        {{
                                            task.items.reduce((a, item) => a + (item.fixedCostBuildTotal ? item.fixedCostBuildTotal : 0), 0) | toCurrency
                                        }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-300 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm font-semibold">Total Cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    <div
                                        v-if="task.items.some((i) => i.markupFixedValue)"
                                        class="text-red-400">
                                        * {{
                                            task.items.reduce((a, item) => a + (item.runningBuildTotal ? item.runningBuildTotal : 0), 0) | toCurrency
                                        }}
                                    </div>
                                    <div v-else>
                                        {{
                                            task.items.reduce((a, item) => a + (item.runningBuildTotal ? item.runningBuildTotal : 0), 0) | toCurrency
                                        }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-300 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Overheads <span
                                    class="text-gray-400">({{ estimate.overheads }}%)</span></td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        task.items.reduce((a, item) => a + (item.overheads ? item.overheads : 0), 0) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Profit <span class="text-gray-400">({{
                                        Math.round(((task.items.reduce((a, item) => a + (item.profit ? item.profit : 0), 0) / task.items.reduce((a, item) => a + (item.buildTotal ? item.buildTotal : item.buildTotalMarkedUp - item.profit - item.overheads), 0)) || (estimate.profit / 100)) * 10000) / 100
                                    }}%)</span></td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        task.items.reduce((a, item) => a + (item.profit ? item.profit : 0), 0) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-300 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm font-semibold">Sub Total</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        task.items.reduce((a, item) => a + (item.buildTotalMarkedUp ? item.buildTotalMarkedUp : 0), 0) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr v-if="task.items.reduce((a, item) => a + (item.discount?item.discount:0),0) || task.items.reduce((a, item) => a + (item.adjustment?item.adjustment:0),0) || task.items.reduce((a, item) => a + (item.mcd?item.mcd:0),0) ">
                                <td colspan="2">
                                    <div class="border-b border-gray-300 my-1"></div>
                                </td>
                            </tr>
                            <tr v-if="task.items.reduce((a, item) => a + (item.discount?item.discount:0),0)">
                                <td class="text-gray-600 text-sm">Less discounts <span
                                    class="text-gray-400">({{
                                        Math.round((task.items.reduce((a, item) => a + (item.discount ? item.discount : 0), 0) / (task.items.reduce((a, item) => a + (item.buildTotalMarkedUp ? item.buildTotalMarkedUp : 0), 0))) * 10000) / 100
                                    }}%)</span>
                                </td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        0 - (task.items.reduce((a, item) => a + (item.discount ? item.discount : 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr v-if="task.items.reduce((a, item) => a + (item.adjustment?item.adjustment:0),0)">
                                <td class="text-gray-600 text-sm">Less adjustment <span
                                    class="text-gray-400">({{ Math.round(estimate.adjustment * 100) / 100 }}%)</span>
                                </td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        0 - (task.items.reduce((a, item) => a + (item.adjustment ? item.adjustment : 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr v-if="task.items.reduce((a, item) => a + (item.mcd?item.mcd:0),0)">
                                <td class="text-gray-600 text-sm">Less MCD <span
                                    class="text-gray-400">({{ estimate.mcd }}%)</span></td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        0 - (task.items.reduce((a, item) => a + (item.mcd ? item.mcd : 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-300 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm font-semibold">Grand Total</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        (task.items.reduce((a, item) => a + (item.finalTotal ? item.finalTotal : 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Effective OHP <span
                                    class="text-gray-400">({{
                                        round((task.items.reduce((a, item) => a + (item.effectiveOHP ? item.effectiveOHP : 0), 0) / (task.items.reduce((a, item) => a + (item.runningBuildTotal ? item.runningBuildTotal : 0), 0))) * 100, 2)
                                    }}%)</span>
                                </td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        (task.items.reduce((a, item) => a + (item.effectiveOHP ? item.effectiveOHP : 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </template>
                </ui-dropdown>
            </div>
            <div class="w-6 flex-shrink-0 ml-2"></div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import {Estimate} from "../../../models";
import {cloneDeep} from "lodash";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import EstimateTaskItem from "./EstimateTaskItem.vue";
import remoteStorage from "../../../utils/remoteStorage";
import * as math from "mathjs";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import {toCurrency} from "../../../utils";

export default {
    components: {
        UiDropdownDivider,
        EstimateTaskItem,
        draggable,
        UiDropdownItem,
    },
    props: {
        estimate: Estimate,
        section: Object,
        task: Object,
        store: Object,
        multimode: false,
        pinging: null,
        clipboardKey: Number,
        clipboardTaskData: Array,
        clipboardItemData: Array,
    },
    data() {
        return {
            newItem: {
                reference: null,
                description: null,
            },
            hasNonDefaultMarkup: false,
        }
    },
    methods: {
        closePasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].close()
            } else {
                this.$refs[p].close()
            }
        },
        openPasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].open(new window.MouseEvent('mouse'))
            } else {
                this.$refs[p].open(new window.MouseEvent('mouse'))
            }
            this.positionPasteMenu(num)
        },
        pasteTask(task, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'task');
            if (at) {
                let num = this.section.tasks.indexOf(at);
                this.section.tasks.splice(num, 0, cloneDeep(task));
            } else {
                this.section.tasks.push(cloneDeep(task));
            }
            this.estimate.update();
            this.estimate.addSnapshot();
            this.$emit('reflow')
        },
        addToLibrary(task) {
            this.$modals.open(
                'estimate-add-item-to-library',
                {
                    name: (task.name + ' - ' + task.reference + ' (' + toCurrency(task.total) + ')').replace(/(\r\n|\n|\r)/gm, ""),
                    title: `Add Task to Library`,
                    button: 'Add Task',
                    useGroups: false,
                    handle: (name, group) => {
                        this.clipboardTaskData.push({
                            name: name,
                            data: cloneDeep(task)
                        })
                        remoteStorage.setItem('estimate-task-clipboard', JSON.stringify(this.clipboardTaskData))
                        remoteStorage.setItem('estimate-clipboard-view', 'task')
                    }
                }
            )
        },
        pasteItem(item, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'item');
            this.task.items.push(cloneDeep(item));
            this.estimate.update();
            this.estimate.addSnapshot();
            this.$emit('reflow')
        },
        positionPasteMenu(num) {
            this.$nextTick(() => {
                if (this.$refs["paste" + num + "relative"][0]) {
                    this.$refs["paste" + num + "relative"][0].setAttribute('style', '')
                } else {
                    this.$refs["paste" + num + "relative"].setAttribute('style', '')
                }
                this.$nextTick(() => {
                    let opener, box
                    if (this.$refs["paste" + num + "item"][0]) {
                        opener = this.$refs["paste" + num + "item"][0].$refs.submenu.getBoundingClientRect()
                    } else {
                        opener = this.$refs["paste" + num + "item"].$refs.submenu.getBoundingClientRect()
                    }
                    if (this.$refs["paste" + num + "menu"][0]) {
                        box = this.$refs["paste" + num + "menu"][0].$el.getBoundingClientRect()
                    } else {
                        box = this.$refs["paste" + num + "menu"].$el.getBoundingClientRect()
                    }
                    let left = Math.min(opener.x - box.x - 10, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 10, window.innerHeight - (box.bottom) - 28);
                    if (this.$refs["paste" + num + "relative"][0]) {
                        this.$refs["paste" + num + "relative"][0].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    } else {
                        this.$refs["paste" + num + "relative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    }
                })
            })
        },
        color(x) {
            return this.estimate.colors(x);
        },
        setColor(item, color) {
            item.color = color
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        openColorSubmenu(e) {
            this.$refs["itemcolormenu"].open(new window.MouseEvent('mouse'))
            this.$nextTick(() => {
                this.$refs["itemcolorrelative"].setAttribute('style', '')
                this.$nextTick(() => {
                    const opener = this.$refs.itemcolor.$refs.submenu.getBoundingClientRect()
                    const box = this.$refs["itemcolor"].$el.getBoundingClientRect()
                    let left = Math.min(opener.x - box.x - 40, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 16, window.innerHeight - (box.bottom) - 28);
                    this.$refs["itemcolorrelative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                })
            })
        },
        round(a, b) {
            if (a) {
                return math.round(a, 0 + b);
            }
            return 0;
        },
        addBundleItem() {
            this.task.items.push({
                reference: this.newItem.reference ?? this.nextItemReference(),
                defaultReference: this.nextItemReference(),
                description: 'Bundle name...',
                quantity: 0,
                uom: '',
                rate: 0,
                total: 0,
                discount: 0,
                discountComment: '',
                markup: 0,
                materials: [],
                labour: [],
                fixedCosts: [],
                buildTotal: 0,
                buildTotalMarkedUp: 0,
                color: '#FFFFFF',
                bundleOpened: true,
                bundle: [{
                    reference: '',
                    defaultReference: '',
                    description: '',
                    quantity: 0,
                    uom: '',
                    rate: 0,
                    total: 0,
                    discount: 0,
                    discountComment: '',
                    markup: 0,
                    materials: [],
                    labour: [],
                    fixedCosts: [],
                    buildTotal: 0,
                    buildTotalMarkedUp: 0,
                    color: '#FFFFFF',
                    bundle: null,
                }],
            });
            this.$nextTick(() => {
                this.$refs.items[this.$refs.items.length - 1].$refs.bundleItems[0].$refs.description.focus();
                this.estimate.update()
                this.estimate.addSnapshot(true)
            })
        },
        addItem(trigger) {
            this.task.items.push({
                reference: this.newItem.reference ?? this.nextItemReference(),
                defaultReference: this.nextItemReference(),
                description: this.newItem.description,
                quantity: 0,
                uom: '',
                rate: 0,
                total: 0,
                discount: 0,
                discountComment: '',
                markup: 0,
                materials: [],
                labour: [],
                fixedCosts: [],
                buildTotal: 0,
                buildTotalMarkedUp: 0,
                color: '#FFFFFF',
                bundle: null,
                isNew: true,
            });
            this.$nextTick(() => {
                if (trigger === 'description') {
                    this.$refs.items[this.$refs.items.length - 1].$refs.description.focus()
                } else {
                    this.$refs.items[this.$refs.items.length - 1].$refs.reference.disableSelectAll()
                    this.$refs.items[this.$refs.items.length - 1].$refs.reference.$el.focus()
                    this.$refs.items[this.$refs.items.length - 1].$refs.reference.enableSelectAll()
                }
                this.newItem.reference = null
                this.newItem.description = ''
                this.estimate.update()
                this.estimate.addSnapshot(true)
            })
        },
        nextItemReference() {
            const sectionNumber = this.estimate.scopeOfWorks.indexOf(this.section) + 1;
            const taskNumber = this.section.tasks.indexOf(this.task) + 1;
            const itemNumber = this.task.items.length + 1
            return `${sectionNumber}.${taskNumber}.${itemNumber}`
        },
        deleteTask() {
            this.section.tasks = this.section.tasks.filter((task) => task !== this.task)
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        cutTask() {
            this.clipboardTaskData[0] = cloneDeep(this.task);
            remoteStorage.setItem('estimate-task-clipboard', JSON.stringify(this.clipboardTaskData))
            remoteStorage.setItem('estimate-clipboard-view', 'task');
            this.section.tasks = this.section.tasks.filter((task) => task !== this.task)
            this.estimate.update()
            this.estimate.addSnapshot()
            this.$emit('reflow')
        },
        copyTask() {
            this.clipboardTaskData[0] = cloneDeep(this.task);
            remoteStorage.setItem('estimate-task-clipboard', JSON.stringify(this.clipboardTaskData))
            remoteStorage.setItem('estimate-clipboard-view', 'task');
        },
        groupedClipboardTaskData() {
            let ret = [{name: '', items: []}];
            this.clipboardItemData.forEach((i, index) => {
                if (index > 0) {
                    let groupName = ''
                    if (i.group) {
                        groupName = i.group
                    }
                    let group = ret.find((a) => a.name === groupName)
                    if (!group) {
                        group = {name: groupName, items: []}
                        ret.push(group)
                    }
                    group.items.push(i)
                }
            })
            return ret
        },
    },
}
</script>
